import { useAtomValue } from "jotai";
import { useRef, useState } from "react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";
import { inputClass } from "@/lib/forms";
import { CodeMatchBadge } from "@/modules/plus/photos/components/CodeMatchBadge";
import { validatePhotoFile } from "@/modules/plus/photos/plus.photos.api";
import type { GalleryPhoto } from "@/modules/plus/photos/plus.photos.atoms";
import { atoms } from "@/state";

interface PhotoEditDialogProps {
  photo: GalleryPhoto | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PhotoEditDialog: React.FC<PhotoEditDialogProps> = ({
  photo,
  isOpen,
  onOpenChange,
}) => (
  <ModalOverlay
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    isDismissable
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-6"
  >
    <Modal className="max-h-full w-full max-w-4xl outline-none">
      <Dialog
        className="rounded-xl bg-white shadow-2xl outline-none dark:bg-gray-950"
        aria-label="Edit photo"
      >
        {/* Keyed by imagePath so opening a different photo remounts the form
            with fresh local state (caption draft, re-link input). */}
        {photo && (
          <PhotoEditForm key={photo.imagePath} photo={photo} onClose={() => onOpenChange(false)} />
        )}
      </Dialog>
    </Modal>
  </ModalOverlay>
);

interface PhotoEditFormProps {
  photo: GalleryPhoto;
  onClose: () => void;
}

const PhotoEditForm: React.FC<PhotoEditFormProps> = ({ photo, onClose }) => {
  const [caption, setCaption] = useState(photo.caption ?? "");
  const [newCode, setNewCode] = useState("");
  const setCaptionMut = useAtomValue(atoms.photos.setCaption);
  const moveMut = useAtomValue(atoms.photos.movePhoto);
  const replaceMut = useAtomValue(atoms.photos.replace);
  const removeMut = useAtomValue(atoms.photos.remove);
  const fileInput = useRef<HTMLInputElement>(null);

  const trimmedNewCode = newCode.trim();
  const captionDirty = (photo.caption ?? "") !== caption;
  const busy =
    setCaptionMut.isPending || moveMut.isPending || replaceMut.isPending || removeMut.isPending;

  const onSaveCaption = async () => {
    try {
      await setCaptionMut.mutateAsync({
        code: photo.code,
        imagePath: photo.imagePath,
        caption,
      });
      toast.success("Caption saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    }
  };

  const onRelink = async () => {
    if (!trimmedNewCode || trimmedNewCode === photo.code) {
      return;
    }
    try {
      await moveMut.mutateAsync({
        oldCode: photo.code,
        newCode: trimmedNewCode,
        imagePath: photo.imagePath,
      });
      toast.success(`Re-linked to ${trimmedNewCode}`);
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Re-link failed");
    }
  };

  const onReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validatePhotoFile(file);
    if (err) {
      toast.error(err);
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    try {
      await replaceMut.mutateAsync({
        code: photo.code,
        imagePath: photo.imagePath,
        file,
      });
      toast.success("Photo replaced");
      onClose();
    } catch (mutErr) {
      toast.error(mutErr instanceof Error ? mutErr.message : "Replace failed");
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete this photo under "${photo.code}"? This can't be undone.`)) {
      return;
    }
    try {
      await removeMut.mutateAsync({ code: photo.code, imagePath: photo.imagePath });
      toast.success("Photo deleted");
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  };

  return (
    <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex items-center justify-center rounded-lg bg-gray-50 p-3 dark:bg-gray-900">
        <img
          src={photo.url}
          alt={photo.caption ?? photo.code}
          className="max-h-[70vh] w-full rounded object-contain"
        />
      </div>

      <div className="flex flex-col gap-5 text-sm">
        <header>
          <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Code</div>
          <div className="mt-0.5 font-mono text-sm text-amber-900 dark:text-amber-200">
            {photo.code}
          </div>
          <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
            Uploaded by {photo.uploadedBy}
          </div>
        </header>

        <section className="flex flex-col gap-2">
          <label
            htmlFor="photo-caption"
            className="text-xs font-medium text-gray-700 dark:text-gray-300"
          >
            Caption
          </label>
          <textarea
            id="photo-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={2}
            className={`${inputClass} resize-y`}
            placeholder="e.g. Left-hand-drive variant"
            disabled={busy}
          />
          <button
            type="button"
            disabled={!captionDirty || busy}
            onClick={onSaveCaption}
            className="self-start rounded-md border border-blue-500 bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {setCaptionMut.isPending ? "Saving…" : "Save caption"}
          </button>
        </section>

        <section className="flex flex-col gap-2 border-t border-gray-200 pt-4 dark:border-gray-800">
          <label
            htmlFor="photo-newcode"
            className="text-xs font-medium text-gray-700 dark:text-gray-300"
          >
            Re-link to another code
          </label>
          <input
            id="photo-newcode"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder="e.g. 4360-RND-06-24x24"
            className={`${inputClass} font-mono`}
            disabled={busy}
          />
          <CodeMatchBadge code={trimmedNewCode} mismatchLabel="will not attach to a model" />
          <button
            type="button"
            disabled={!trimmedNewCode || trimmedNewCode === photo.code || busy}
            onClick={onRelink}
            className="self-start rounded-md border border-blue-500 bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {moveMut.isPending ? "Moving…" : "Re-link photo"}
          </button>
        </section>

        <section className="flex flex-col gap-2 border-t border-gray-200 pt-4 dark:border-gray-800">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-300">Replace image</div>
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
            className="self-start rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
          >
            {replaceMut.isPending ? "Uploading…" : "Choose new file…"}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onReplaceFile}
            className="hidden"
          />
        </section>

        <section className="mt-auto flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-800">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            {removeMut.isPending ? "Deleting…" : "Delete photo"}
          </button>
        </section>
      </div>
    </div>
  );
};
